import api from '@/api/client';
import { servicePath } from '@/config/env';
import { getInitials } from '@/lib/getInitials';
import type { AddableClassMember } from '@/features/class/types';

/** Shape of a single board item returned by `/midl/api/v3/board/all/joined`. */
export interface JoinedBoard {
  id: number;
  name: string;
  code?: string;
  isJoined?: number;
}

export const BOARD_REQUEST_NOT_JOINED = 0;
export const BOARD_REQUEST_PENDING = 1;
export const BOARD_REQUEST_JOINED = 2;

export type BoardMembershipStatus =
  | typeof BOARD_REQUEST_NOT_JOINED
  | typeof BOARD_REQUEST_PENDING
  | typeof BOARD_REQUEST_JOINED;

export interface BoardJoinedRequestPayload {
  boardId: number;
  joinedBy: number;
}

export interface BoardMembershipResponseData {
  id?: number;
  boardId?: number;
  joinedBy?: number;
  isJoined?: BoardMembershipStatus;
  joinBoardCount?: number;
  [key: string]: unknown;
}

interface BoardMembershipEnvelope {
  statusCode?: string;
  data?: BoardMembershipResponseData;
}

export function toBoardMembershipStatus(value: unknown): BoardMembershipStatus | null {
  const status = Number(value);
  if (
    status === BOARD_REQUEST_NOT_JOINED ||
    status === BOARD_REQUEST_PENDING ||
    status === BOARD_REQUEST_JOINED
  ) {
    return status;
  }
  return null;
}

function normalizeBoardMembershipResponse(
  body: BoardMembershipEnvelope | BoardMembershipResponseData | undefined,
  fallbackStatus: BoardMembershipStatus,
): BoardMembershipResponseData {
  const data =
    body && typeof body === 'object' && 'data' in body
      ? ((body as BoardMembershipEnvelope).data ?? {})
      : ((body as BoardMembershipResponseData | undefined) ?? {});
  const responseStatus = toBoardMembershipStatus(data.isJoined);
  return {
    ...data,
    isJoined: responseStatus ?? fallbackStatus,
  };
}

interface JoinedBoardsEnvelope {
  statusCode?: string;
  data?: JoinedBoard[];
}

/**
 * GET /midl/api/v3/board/all/joined?userId=<id>&isIncludeOwn=<bool>
 *
 * Returns the list of classes (boards) the user has joined.
 */
export async function fetchJoinedBoards(
  userId: number | string,
  options: { includeOwn?: boolean } = {},
): Promise<JoinedBoard[]> {
  const { includeOwn = false } = options;
  const response = await api.get<JoinedBoardsEnvelope | JoinedBoard[]>(
    servicePath('midl', 'board/all/joined'),
    { params: { userId, isIncludeOwn: includeOwn } },
  );

  const body = response.data;
  if (Array.isArray(body)) return body;
  return body?.data ?? [];
}

// ---------------------------------------------------------------------------
// Board detail
// ---------------------------------------------------------------------------

/** Shape of the owner / admin object commonly returned by board detail APIs. */
export interface BoardOwner {
  id?: number;
  name?: string;
  surName?: string;
  fullName?: string;
  displayName?: string;
  username?: string;
}

/** Subset of the board detail payload the calendar cares about. */
export interface BoardDetail {
  id: number;
  code?: string;
  name?: string;
  boardName?: string;
  className?: string;
  isPrivate?: boolean;
  classType?: number | string | Record<string, unknown>;
  classTypeId?: number | string;
  subjectId?: number | string | null;
  masterSubjectId?: number | string | null;
  subject?: BoardTaxonomyRef;
  masterSubject?: BoardTaxonomyRef;
  gradeId?: number | string | null;
  masterGradeId?: number | string | null;
  gradeLevelId?: number | string | null;
  grade?: BoardTaxonomyRef;
  masterGrade?: BoardTaxonomyRef;
  gradeLevel?: BoardTaxonomyRef;
  schoolYear?: string;
  academicYearId?: number | string;
  academicYear?: number | string;
  credit?: number | string;
  credits?: number | string;
  notifyToFollowers?: boolean;
  notifyFollowers?: boolean;
  ownerName?: string;
  owner?: BoardOwner;
  /** True when the current user's account is linked to a roster entry. */
  linkedWithNamelist?: boolean;
  [key: string]: unknown;
}

/** A backend taxonomy reference (subject / grade) — `{ id, name }`. */
export interface BoardTaxonomyRef {
  id: number;
  name: string;
}

/**
 * GET /midl/api/v3/board/{boardId}
 *
 * Returns the full board detail object — used by the calendar to resolve the
 * board owner's display name for each period.
 */
export async function fetchBoardDetail(boardId: number | string): Promise<BoardDetail | null> {
  const response = await api.get<unknown>(servicePath('midl', `board/${boardId}`));
  const body = response.data as Record<string, unknown> | undefined;
  if (!body) return null;
  // Envelope: { statusCode, data: BoardDetail }
  if (body.data && typeof body.data === 'object') {
    return body.data as BoardDetail;
  }
  return body as BoardDetail;
}

/** Resolve the owner display name from a board detail payload. */
export function getBoardOwnerName(detail: BoardDetail | null | undefined): string {
  if (!detail) return '';
  if (typeof detail.ownerName === 'string' && detail.ownerName.trim()) {
    return detail.ownerName.trim();
  }
  const owner = detail.owner;
  if (owner && typeof owner === 'object') {
    if (owner.fullName?.trim()) return owner.fullName.trim();
    if (owner.displayName?.trim()) return owner.displayName.trim();
    const first = owner.name?.trim() ?? '';
    const last = owner.surName?.trim() ?? '';
    const joined = `${first} ${last}`.trim();
    if (joined) return joined;
    if (owner.username?.trim()) return owner.username.trim();
  }
  // Some responses expose `createdBy` instead of `owner`.
  const createdBy = (detail as Record<string, unknown>).createdBy;
  if (createdBy && typeof createdBy === 'object') {
    const cb = createdBy as Record<string, unknown>;
    const fullName = typeof cb.fullName === 'string' ? cb.fullName.trim() : '';
    if (fullName) return fullName;
    const name = typeof cb.name === 'string' ? cb.name.trim() : '';
    const surName = typeof cb.surName === 'string' ? cb.surName.trim() : '';
    const joined = `${name} ${surName}`.trim();
    if (joined) return joined;
  }
  return '';
}

/**
 * Shape of a single member entry returned by `/midl/api/v3/board/member`.
 *
 * Note: the API wraps the actual user under `member`, while admin/role flags
 * live on the outer object.
 */
export interface BoardMemberUser {
  id: number;
  userTypeId?: number;
  studentId?: string;
  name?: string;
  surName?: string;
  username?: string;
  email?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
  isPremiumUser?: boolean;
  isPrivate?: boolean;
}

export interface BoardMember {
  id: number;
  isAdmin: number;
  member: BoardMemberUser;
  /**
   * Some payloads surface a *pending* admin-invitation record id on the member
   * row (the value `board/admin/cancel-invitation` needs). The exact key has
   * varied across deployments, so all known aliases are optional here and
   * resolved by {@link getBoardMemberPendingAdminRequestId}.
   */
  adminInvitationId?: number;
  adminRequestId?: number;
  invitationId?: number;
  requestId?: number;
  /** Pending-admin flags some payloads use instead of (or with) an id. */
  isAdminInvited?: boolean;
  adminInvitationStatus?: string;
  [key: string]: unknown;
}

/** Keys a `BoardMember` row might use for a pending admin-invitation record id. */
const BOARD_MEMBER_REQUEST_ID_FIELDS = [
  'adminInvitationId',
  'adminRequestId',
  'invitationId',
  'requestId',
  'adminInviteId',
  'sentRequestId',
] as const;

/**
 * The cancel-invitation `requestId` is the invited member's **board-join
 * record id** — the *outer* `id` on a `/board/member` row (NOT `member.id`,
 * which is the user id). This is the same id space the accept endpoint
 * (`PUT /board/join`) echoes back as `data.id`.
 *
 * Returns the join-record id for a member row, preferring any explicit
 * invitation-id alias and falling back to the outer `id`.
 */
export function getBoardMemberJoinRecordId(member: BoardMember): number | undefined {
  for (const key of BOARD_MEMBER_REQUEST_ID_FIELDS) {
    const value = member[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  // The outer board-member `id` is the join-record id used by cancel-invitation.
  return typeof member.id === 'number' && Number.isFinite(member.id) ? member.id : undefined;
}

/**
 * Extract a pending admin-invitation `requestId` from a member row, if it
 * carries one. Returns `undefined` when the row has no pending-invite id.
 *
 * @deprecated Prefer {@link getBoardMemberJoinRecordId}: the cancel id is the
 * board-join record id (outer `id`), which that helper resolves directly.
 */
export function getBoardMemberPendingAdminRequestId(member: BoardMember): number | undefined {
  return getBoardMemberJoinRecordId(member);
}

interface BoardMembersEnvelope {
  statusCode?: string;
  totalCount?: number;
  data?: BoardMember[];
}

/**
 * GET /midl/api/v3/board/member?boardId=<id>&size=<n>&page=<n>
 *
 * Returns the paginated member list for a class (board).
 */
export async function fetchBoardMembers(
  boardId: number | string,
  options: { size?: number; page?: number } = {},
): Promise<BoardMember[]> {
  const { size = 1000, page = 0 } = options;
  const response = await api.get<BoardMembersEnvelope | BoardMember[]>(
    servicePath('midl', 'board/member'),
    { params: { boardId, size, page } },
  );

  const body = response.data;
  if (Array.isArray(body)) return body;
  return body?.data ?? [];
}

// ---------------------------------------------------------------------------
// Class roster (namelist)
// ---------------------------------------------------------------------------

/**
 * Shape of the optional linked-account `user` block on a namelist entry.
 * Present when the roster student has been linked to a real MyU user
 * (parent / student / etc.).
 */
export interface ClassNamelistUser {
  id: number;
  userTypeId?: number;
  studentId?: string;
  name?: string;
  surName?: string;
  username?: string;
  email?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
  isPrivate?: boolean;
  hideReadReceipt?: boolean;
}

/**
 * Single entry returned by `/midl/api/v3/class/namelist/<classCode>`.
 *
 * Each entry represents a roster row (the student "name slot"). The optional
 * `user` field is the linked MyU account, if any.
 */
export interface ClassNamelistEntry {
  id: number;
  fullname: string;
  studentId: string;
  position: number;
  user: ClassNamelistUser | null;
  /** All accounts linked to this roster row. Prefer this over legacy `user`. */
  users: ClassNamelistUser[];
}

interface ClassNamelistEnvelope {
  statusCode?: string;
  data?: ClassNamelistEntry[];
}

/**
 * GET /midl/api/v3/class/namelist/<classCode>?size=<n>&page=<n>
 *
 * Returns the class roster — the ordered list of student "name slots",
 * each optionally linked to a real MyU user account.
 */
export async function fetchClassNamelist(
  classCode: number | string,
  options: { size?: number; page?: number } = {},
): Promise<ClassNamelistEntry[]> {
  const { size = 1000, page = 0 } = options;
  const path = servicePath('midl', `class/namelist/${classCode}`);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] GET ${path}`, { classCode, size, page });
  }

  const response = await api.get<ClassNamelistEnvelope | ClassNamelistEntry[]>(path, {
    params: { size, page },
  });

  const body = response.data;
  const result = Array.isArray(body) ? body : (body?.data ?? []);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] GET ${path} SUCCESS`, {
      classCode,
      resultCount: result.length,
      isArray: Array.isArray(body),
      result: result.slice(0, 3),
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Roster → "Link account" picker
// ---------------------------------------------------------------------------

/**
 * Single entry returned by
 * `GET /midl/api/v3/class/namelist/classmember/<boardId>`.
 *
 * The endpoint feeds the roster's "Link account" modal — i.e. the list of
 * class members that can be linked to a roster name slot. Field names mirror
 * the rest of the MyU member payloads; the response envelope is the usual
 * `{ statusCode, data: [...] }` (sometimes a bare array).
 */
export interface ClassNamelistMember {
  id: number;
  userTypeId?: number;
  studentId?: string;
  name?: string;
  surName?: string;
  username?: string;
  email?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
  isPremiumUser?: boolean;
  isPrivate?: boolean;
  /**
   * Some backends nest the user under a `member` field (same shape as
   * `BoardMember`). Optional so both flat and nested shapes deserialize.
   */
  member?: Partial<ClassNamelistMember>;
}

/**
 * Defensive extraction of a `ClassNamelistMember[]` from whatever envelope
 * the MyU backend chose to wrap the response in (the namelist endpoints have
 * historically used several different shapes: bare array, `{ data: [...] }`,
 * `{ result: [...] }`, `{ content: [...] }`, or even nested under
 * `{ data: { content: [...] } }`).
 */
function extractClassNamelistMembers(payload: unknown): ClassNamelistMember[] {
  if (Array.isArray(payload)) return payload as ClassNamelistMember[];
  if (!payload || typeof payload !== 'object') return [];

  const root = payload as Record<string, unknown>;
  const dataObj = root.data as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    root.data,
    root.result,
    root.results,
    root.content,
    root.items,
    root.members,
    root.classMembers,
    root.namelistMembers,
    dataObj?.content,
    dataObj?.items,
    dataObj?.results,
    dataObj?.members,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as ClassNamelistMember[];
  }
  return [];
}

/**
 * POST /midl/api/v3/class/namelist/by-member
 *
 * Adds class members directly to the roster in a single API call.
 * This is the correct endpoint — using the two-step create+link approach
 * causes "student id already exists" / "name already exists" errors for
 * members who already have a namelist slot.
 *
 * body: { "boardId": <number>, "userIds": <number[]> }
 * success response: { "statusCode": "2XX", "data": [] }
 */
export async function addNamelistByMember(
  boardId: number | string,
  userIds: readonly (number | string)[],
): Promise<void> {
  const path = servicePath('midl', 'class/namelist/by-member');
  const body = { boardId: Number(boardId), userIds: userIds.map(Number) };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  await api.post(path, body);
}

/**
 * PUT /midl/api/v3/class/namelist/sort/<namelistEntryId>?debug=true
 *
 * Persists a manual drag-and-drop reorder of the roster.
 * The path param is the **namelist entry id** (the id of the row being dragged).
 * Both positions are 1-based display indices in the current order.
 *
 * body: { "oldPosition": <number>, "newPosition": <number> }
 */
export async function sortNamelistOrder(
  namelistEntryId: number | string,
  oldPosition: number,
  newPosition: number,
): Promise<void> {
  const path = servicePath('midl', `class/namelist/sort/${namelistEntryId}`);
  const body = { oldPosition, newPosition };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] PUT ${path}?debug=true`, body);
  }

  await api.put(path, body, { params: { debug: true } });
}

/**
 * GET /midl/api/v3/class/namelist/classmember/<boardId>?debug=true
 *
 * Returns the list of class members available for linking to roster name
 * slots. Used by `LinkAccountModal`.
 */
export async function fetchClassNamelistMembers(
  boardId: number | string,
  options: { debug?: boolean } = {},
): Promise<ClassNamelistMember[]> {
  const { debug = true } = options;
  const path = servicePath('midl', `class/namelist/classmember/${boardId}`);

  const response = await api.get<unknown>(path, { params: { debug } });
  const members = extractClassNamelistMembers(response.data);

  if (import.meta.env.DEV) {
    console.info(
      `[boardsApi] GET ${path}?debug=${debug} → ${members.length} member(s)`,
      members.length === 0 ? { rawResponse: response.data } : undefined,
    );
  }

  return members;
}

// ---------------------------------------------------------------------------
// Roster → "Link account" SAVE
// ---------------------------------------------------------------------------

export interface UpdateBoardNamelistLinksRequest {
  /** Complete desired collection; an empty array unlinks every account. */
  readonly linkedUserIds: readonly (number | string)[];
}

export interface LinkClassNamelistMemberRequest extends UpdateBoardNamelistLinksRequest {
  /** Numeric class/board id (`universityId`-style). */
  readonly boardId?: number | string;
  /** Numeric id of the roster name-slot (the `id` field of a namelist entry). */
  readonly namelistEntryId: number | string;
}

/**
 * PATCH /midl/api/v3/class/namelist/link-unlink/<namelistEntryId>?debug=true
 *
 * Replaces every user link for a roster name-slot. The collection is
 * deduplicated before sending; an empty collection unlinks every account.
 */
export async function linkClassNamelistMember(
  request: LinkClassNamelistMemberRequest,
): Promise<void> {
  const { namelistEntryId, linkedUserIds } = request;
  const path = servicePath('midl', `class/namelist/link-unlink/${namelistEntryId}`);
  const body = { linkedUserIds: [...new Set(linkedUserIds.map(Number))] };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] PATCH ${path}?debug=true (link)`, body);
  }

  await api.patch(path, body, { params: { debug: true } });
}

/**
 * PATCH /midl/api/v3/class/namelist/link-unlink/<namelistEntryId>
 *
 * Persist an unlink by replacing the complete linked-user collection. Pass
 * an empty `linkedUserIds` array to remove every account from the row.
 */
export async function unlinkClassNamelistMember(
  request: Pick<LinkClassNamelistMemberRequest, 'namelistEntryId' | 'linkedUserIds'>,
): Promise<void> {
  await linkClassNamelistMember(request);
}

// ---------------------------------------------------------------------------
// Roster → DELETE a name-slot
// ---------------------------------------------------------------------------

/**
 * DELETE /midl/api/v3/class/namelist/<namelistEntryId>
 *
 * Removes a single roster name-slot (and, by side effect on the backend,
 * any link to a MyU user that slot had). Verified contract (from the live
 * web curl): no body, `content-length: 0`, returns the deleted entry in a
 * `{ statusCode, data }` envelope.
 *
 * Wired from `useRosterState.removeStudent` so the "Remove from roster"
 * action in the confirm modal persists to the backend instead of being a
 * client-only edit.
 */
export async function deleteClassNamelistEntry(namelistEntryId: number | string): Promise<void> {
  const path = servicePath('midl', `class/namelist/${namelistEntryId}`);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] DELETE ${path}`);
  }

  await api.delete(path);
}

// ---------------------------------------------------------------------------
// Roster → EDIT a name-slot
// ---------------------------------------------------------------------------

export interface UpdateClassNamelistEntryRequest {
  /** Numeric class/board id the slot belongs to. */
  readonly boardId: number | string;
  /** Numeric id of the roster name-slot being edited. */
  readonly namelistEntryId: number | string;
  /** New display name for the slot. */
  readonly fullname: string;
  /**
   * Optional roster-side student id (the school-issued identifier shown
   * under the name). Sent only when present so we don't accidentally
   * clear it on the server.
   */
  readonly studentId?: string;
}

/**
 * PUT /midl/api/v3/class/namelist/<namelistEntryId>?debug=true
 *
 * Updates the editable fields of a single roster name-slot. Verified
 * contract (from the live web curl):
 *   PUT …/class/namelist/{namelistEntryId}?debug=true
 *   body: { "boardId": <number>, "fullname": "<string>" }
 *
 * Wired from `useRosterState.updateStudent` so the "Edit student" modal
 * persists changes to the backend instead of being a client-only edit.
 */
export async function updateClassNamelistEntry(
  request: UpdateClassNamelistEntryRequest,
): Promise<void> {
  const { boardId, namelistEntryId, fullname, studentId } = request;
  const path = servicePath('midl', `class/namelist/${namelistEntryId}`);
  const body: Record<string, unknown> = {
    boardId: Number(boardId),
    fullname,
  };
  if (studentId && studentId.trim()) {
    body.studentId = studentId.trim();
  }

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] PUT ${path}?debug=true`, body);
  }

  await api.put(path, body, { params: { debug: true } });
}

// ---------------------------------------------------------------------------
// Roster → CREATE a name-slot (manual entry)
// ---------------------------------------------------------------------------

export interface CreateClassNamelistEntryRequest {
  /** Numeric class/board id the new slot belongs to. */
  readonly boardId: number | string;
  /** Display name for the new roster slot. */
  readonly fullname: string;
  /** Optional school-issued student id. */
  readonly studentId?: string;
}

/**
 * Subset of the {@link ClassNamelistEntry} fields the create endpoint
 * echoes back. The MyU backend wraps the new row in the usual
 * `{ statusCode, data }` envelope; we only need `id` to seed the
 * optimistic UI row with the real server id.
 */
interface CreateClassNamelistEntryEnvelope {
  statusCode?: string;
  data?: ClassNamelistEntry;
}

/**
 * POST /midl/api/v3/class/namelist
 *
 * Creates a single roster name-slot. Verified contract (from the live web
 * curl):
 *   POST …/class/namelist
 *   body: { "boardId": <number>, "fullname": "<string>", "studentId"?: "<string>" }
 *
 * Wired from `useRosterState.addManualStudent` (the "Enter manually"
 * option inside the Add student modal).
 */
export async function createClassNamelistEntry(
  request: CreateClassNamelistEntryRequest,
): Promise<ClassNamelistEntry | null> {
  const { boardId, fullname, studentId } = request;
  const path = servicePath('midl', 'class/namelist');
  const body: Record<string, unknown> = {
    boardId: Number(boardId),
    fullname,
  };
  if (studentId && studentId.trim()) {
    body.studentId = studentId.trim();
  }

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<CreateClassNamelistEntryEnvelope | ClassNamelistEntry>(
    path,
    body,
  );
  const payload = response.data as
    | CreateClassNamelistEntryEnvelope
    | ClassNamelistEntry
    | undefined;
  if (!payload) return null;
  if ('data' in payload && payload.data) return payload.data;
  if ('id' in payload) return payload as ClassNamelistEntry;
  return null;
}

// ---------------------------------------------------------------------------
// Roster → Institution directory
// ---------------------------------------------------------------------------

/**
 * Shape of a single entry returned by
 * `/midl/api/v3/class/institution_directory/<boardId>`.
 *
 * Each entry is a student record from the school's central directory that
 * the teacher can add to the class roster. Field names mirror the rest of
 * the MyU member payloads; the response envelope is the usual
 * `{ statusCode, data: [...] }` (sometimes a bare array).
 *
 * The numeric `studentInfoId` is the id required by the
 * `class/namelist/institution_directory/<boardId>` POST contract — keep
 * that field intact when mapping into UI shapes.
 */
export interface InstitutionDirectoryApiEntry {
  studentInfoId: number;
  name?: string;
  surName?: string;
  fullname?: string;
  studentId?: string;
}

/** Defensive extraction — mirrors {@link extractClassNamelistMembers}. */
function extractInstitutionDirectory(payload: unknown): InstitutionDirectoryApiEntry[] {
  if (Array.isArray(payload)) return payload as InstitutionDirectoryApiEntry[];
  if (!payload || typeof payload !== 'object') return [];
  const root = payload as Record<string, unknown>;
  const dataObj = root.data as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    root.data,
    root.result,
    root.results,
    root.content,
    root.items,
    dataObj?.content,
    dataObj?.items,
    dataObj?.results,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as InstitutionDirectoryApiEntry[];
  }
  return [];
}

/**
 * GET /midl/api/v3/class/institution_directory/<boardId>
 *
 * Returns the school's central student directory available for adding to
 * this class's roster. Used by the "Institution directory" tab in the Add
 * student modal.
 */
export async function fetchInstitutionDirectory(
  boardId: number | string,
): Promise<InstitutionDirectoryApiEntry[]> {
  const path = servicePath('midl', `class/institution_directory/${boardId}`);
  const response = await api.get<unknown>(path);
  const entries = extractInstitutionDirectory(response.data);

  if (import.meta.env.DEV) {
    console.info(
      `[boardsApi] GET ${path} → ${entries.length} directory entr${entries.length === 1 ? 'y' : 'ies'}`,
      entries.length === 0 ? { rawResponse: response.data } : undefined,
    );
  }
  return entries;
}

export interface AddInstitutionDirectoryStudentsRequest {
  /** Numeric class/board id. */
  readonly boardId: number | string;
  /** Numeric `studentInfoId`s of the directory entries to add. */
  readonly studentInfoIds: readonly (number | string)[];
  /** Source tag the backend wants — defaults to `'app'`. */
  readonly sourceType?: string;
}

/**
 * POST /midl/api/v3/class/namelist/institution_directory/<boardId>
 *
 * Adds one or more institution-directory students to the class roster as
 * brand-new namelist entries. Verified contract (from the live web curl):
 *   POST …/class/namelist/institution_directory/{boardId}
 *   body: { "studentInfoIds": [<number>, …], "sourceType": "app" }
 */
export async function addInstitutionDirectoryStudents(
  request: AddInstitutionDirectoryStudentsRequest,
): Promise<void> {
  const { boardId, studentInfoIds, sourceType = 'app' } = request;
  const path = servicePath('midl', `class/namelist/institution_directory/${boardId}`);
  const body = {
    studentInfoIds: studentInfoIds.map((id) => Number(id)),
    sourceType,
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  await api.post(path, body);
}

// ---------------------------------------------------------------------------
// Board (class) → CREATE
// ---------------------------------------------------------------------------

export interface CreateBoardRequest {
  /** Class name as typed in the form. */
  readonly name: string;
  /** Whether the class is private. */
  readonly isPrivate: boolean;
  /** Numeric id of the owner (current authenticated user). */
  readonly userId: number | string;
  /** Device tag the backend wants — defaults to `'web'`. */
  readonly deviceType?: string;
  readonly categoryId?: number | string | null;
  /** Optional subject selected from `master/getdata?masters=subjects,recentSubjects`. */
  readonly subjectId?: number | string | null;
  /** Grade selected from `master/getdata?masters=grades,recentGrades`. */
  readonly gradeId?: number | string | null;
  readonly description?: string;
  readonly credit?: string;
  readonly currency?: string;
  readonly amount?: number;
  readonly notifyToFollowers?: boolean;
  readonly connectingAllowed?: boolean | null;
  readonly schoolYear?: string;
  readonly classType: 1 | 2 | 3;
}

function toNullableNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Shape of the board record echoed back by `POST /midl/api/v3/board`.
 * Mirrors the fields the rest of the app already deals with for boards.
 */
export interface CreatedBoard {
  id: number;
  name: string;
  code?: string;
  isPrivate?: boolean;
}

interface CreateBoardEnvelope {
  statusCode?: string;
  data?: CreatedBoard;
}

/**
 * POST /midl/api/v3/board
 *
 * Creates a new class (board). Verified contract (from the live web
 * curl):
 *   POST …/board
 *   body: { name, categoryId, userId, gradeId, subjectId, description, credit, currency, amount, notifyToFollowers, connectingAllowed, schoolYear, classType }
 *
 * Wired from `ClassFormModal` so the "Create class" submit hits the real
 * backend in addition to the existing optimistic local-store insert.
 */
export async function createBoard(request: CreateBoardRequest): Promise<CreatedBoard | null> {
  const {
    name,
    isPrivate,
    userId,
    deviceType = 'web',
    categoryId = null,
    subjectId = null,
    gradeId = null,
    description = '',
    credit = '',
    currency = '',
    amount = 0,
    notifyToFollowers = true,
    connectingAllowed = null,
    schoolYear = '',
    classType,
  } = request;
  const path = servicePath('midl', 'board');
  const body = {
    name,
    categoryId: toNullableNumber(categoryId),
    userId: Number(userId),
    gradeId: toNullableNumber(gradeId),
    subjectId: toNullableNumber(subjectId),
    description,
    credit,
    currency,
    amount,
    notifyToFollowers,
    connectingAllowed,
    schoolYear,
    classType,
    isPrivate,
    deviceType,
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<CreateBoardEnvelope | CreatedBoard>(path, body);
  const payload = response.data as CreateBoardEnvelope | CreatedBoard | undefined;
  if (!payload) return null;
  if ('data' in payload && payload.data) return payload.data;
  if ('id' in payload) return payload as CreatedBoard;
  return null;
}

// ---------------------------------------------------------------------------
// Board → DELETE
// ---------------------------------------------------------------------------

export interface DeleteBoardRequest {
  /** Numeric board id to delete. */
  readonly boardId: number | string;
  /** Numeric id of the owner performing the delete (current authed user). */
  readonly userId: number | string;
}

/**
 * DELETE /midl/api/v3/board?id=<boardId>&userId=<userId>
 *
 * Permanently deletes a class (board). Verified contract (from the live web
 * curl): a bodyless DELETE with the board id and owner id in the query string.
 * The `myu-auth-token`, `app-client-key`, `appversion` and `devicetype`
 * headers are attached automatically by the shared axios client.
 *
 * Wired from `ClassFormModal` so the "Delete class" action hits the real
 * backend in addition to the optimistic local-store removal.
 */
export async function deleteBoard(request: DeleteBoardRequest): Promise<void> {
  const { boardId, userId } = request;
  const path = servicePath('midl', 'board');

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] DELETE ${path}`, { id: boardId, userId });
  }

  await api.delete(path, {
    params: { id: Number(boardId), userId: Number(userId) },
  });
}

// ---------------------------------------------------------------------------
// Board → UPDATE
// ---------------------------------------------------------------------------

export interface UpdateBoardRequest {
  /** Numeric board id to update. */
  readonly boardId: number | string;
  /** New class name. */
  readonly name: string;
  /** New privacy flag. */
  readonly isPrivate: boolean;
  /** Numeric id of the owner performing the update (current authed user). */
  readonly userId: number | string;
  /** Device tag the backend wants — defaults to `'web'`. */
  readonly deviceType?: string;
  readonly categoryId?: number | string | null;
  /** Optional subject selected from `master/getdata?masters=subjects,recentSubjects`. */
  readonly subjectId?: number | string | null;
  /** Grade selected from `master/getdata?masters=grades,recentGrades`. */
  readonly gradeId?: number | string | null;
  readonly description?: string;
  readonly credit?: string;
  readonly currency?: string;
  readonly amount?: number;
  readonly notifyToFollowers?: boolean;
  readonly connectingAllowed?: boolean | null;
  readonly schoolYear?: string;
  readonly classType?: 1 | 2 | 3;
}

interface UpdateBoardEnvelope {
  statusCode?: string;
  data?: CreatedBoard;
}

/**
 * PUT /midl/api/v3/board/<boardId>
 *
 * Updates an existing class (board). Verified contract (from the live web
 * curl):
 *   PUT …/board/<boardId>
 *   body: { name, categoryId, userId, gradeId, subjectId, description, credit, currency, amount, notifyToFollowers, connectingAllowed, schoolYear, classType, isPrivate, deviceType }
 *
 * The `myu-auth-token`, `app-client-key`, `appversion` and `devicetype`
 * headers are attached automatically by the shared axios client. Wired from
 * `ClassFormModal` so the "Save changes" submit persists edits to the real
 * backend in addition to the optimistic local-store update.
 */
export async function updateBoard(request: UpdateBoardRequest): Promise<CreatedBoard | null> {
  const {
    boardId,
    name,
    isPrivate,
    userId,
    deviceType = 'web',
    categoryId,
    subjectId,
    gradeId,
    description,
    credit,
    currency,
    amount,
    notifyToFollowers,
    connectingAllowed,
    schoolYear,
    classType,
  } = request;
  const path = servicePath('midl', `board/${boardId}`);
  const body = {
    name,
    isPrivate,
    userId: Number(userId),
    deviceType,
    ...(categoryId !== undefined ? { categoryId: toNullableNumber(categoryId) } : {}),
    ...(subjectId !== undefined ? { subjectId: toNullableNumber(subjectId) } : {}),
    ...(gradeId !== undefined ? { gradeId: toNullableNumber(gradeId) } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(credit !== undefined ? { credit } : {}),
    ...(currency !== undefined ? { currency } : {}),
    ...(amount !== undefined ? { amount } : {}),
    ...(notifyToFollowers !== undefined ? { notifyToFollowers } : {}),
    ...(connectingAllowed !== undefined ? { connectingAllowed } : {}),
    ...(schoolYear !== undefined ? { schoolYear } : {}),
    ...(classType !== undefined ? { classType } : {}),
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] PUT ${path}`, body);
  }

  const response = await api.put<UpdateBoardEnvelope | CreatedBoard>(path, body);
  const payload = response.data as UpdateBoardEnvelope | CreatedBoard | undefined;
  if (!payload) return null;
  if ('data' in payload && payload.data) return payload.data;
  if ('id' in payload) return payload as CreatedBoard;
  return null;
}

// ---------------------------------------------------------------------------
// Board → ARCHIVE
// ---------------------------------------------------------------------------

export interface ArchiveBoardRequest {
  /** Numeric board id to archive. */
  readonly boardId: number | string;
}

/**
 * POST /midl/api/v3/archive-board/<boardId>
 *
 * Archives an existing class (board). The board id is the only variable — it
 * lives in the path, and the request carries no body. The `myu-auth-token`,
 * `app-client-key`, `appversion` and `devicetype` headers are attached
 * automatically by the shared axios client.
 *
 * NOTE: The backend rejects PUT on this route ("Request method 'PUT' is not
 * supported", HTTP 400), so the archive action is sent as POST.
 *
 * Wired from `ClassFormModal` so the "Archive class" action hits the real
 * backend in addition to the optimistic local-store archive.
 */
export async function archiveBoard(request: ArchiveBoardRequest): Promise<void> {
  const { boardId } = request;
  const path = servicePath('midl', `archive-board/${boardId}`);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`);
  }

  await api.post(path);
}

// ---------------------------------------------------------------------------
// Board → UNARCHIVE (move back to profile / make active again)
// ---------------------------------------------------------------------------

export interface UnarchiveBoardRequest {
  /** Numeric board id to unarchive (move back to the active profile list). */
  readonly boardId: number | string;
}

/**
 * POST /midl/api/v3/un-archive-board/<boardId>
 *
 * Un-archives an existing class (board) — i.e. "Move to profile" — so it
 * returns to the owner's active class list. Mirrors `archiveBoard`: the board
 * id lives in the path, the request carries no body, and the auth headers are
 * attached automatically by the shared axios client.
 *
 * Verified contract (from the live web curl):
 *   POST https://beta-newapi.myu.co/midl/api/v3/un-archive-board/<boardId>
 * Note the hyphen — the route is `un-archive-board`, NOT `unarchive-board`.
 * The wrong spelling returned the MyU gateway's generic `{ statusCode: "401",
 * errorMessage: "Authenication error" }` envelope (its "I don't recognise this
 * request" response), which previously bubbled up and logged the user out.
 *
 * Wired from `ClassFormModal` so the archived-classes "Move to profile" action
 * hits the real backend in addition to the optimistic local-store restore.
 */
export async function unarchiveBoard(request: UnarchiveBoardRequest): Promise<void> {
  const { boardId } = request;
  const path = servicePath('midl', `un-archive-board/${boardId}`);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`);
  }

  await api.post(path);
}

// ---------------------------------------------------------------------------
// Class category → CREATE
// ---------------------------------------------------------------------------

export interface CreateClassCategoryRequest {
  /** Display name for the new profile class category. */
  readonly name: string;
}

/** Normalized profile class category (named group; excludes "uncategorised"). */
export interface ClassCategory {
  id: number;
  name: string;
}

/** Raw category row as returned by the backend (field naming varies). */
export interface ClassCategoryApiEntry {
  id?: number | string;
  categoryId?: number | string;
  name?: string;
  categoryName?: string;
}

/** Defensive extraction — mirrors {@link extractInstitutionDirectory}. */
function extractClassCategories(payload: unknown): ClassCategoryApiEntry[] {
  if (Array.isArray(payload)) return payload as ClassCategoryApiEntry[];
  if (!payload || typeof payload !== 'object') return [];
  const root = payload as Record<string, unknown>;
  const dataObj = root.data as Record<string, unknown> | undefined;
  const candidates: unknown[] = [
    root.data,
    root.result,
    root.results,
    root.content,
    root.items,
    root.categories,
    dataObj?.content,
    dataObj?.items,
    dataObj?.results,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as ClassCategoryApiEntry[];
  }
  return [];
}

/**
 * GET /midl/api/v3/class/category
 *
 * Returns the full set of the user's named profile class categories —
 * including empty ones the login `boardWithCategory[]` payload omits. Used
 * as the authoritative category source for the Manage categories popup.
 *
 * The "uncategorised" pseudo-category (`id === -1` / null) is excluded so
 * callers only get real named categories.
 */
export async function fetchClassCategories(): Promise<ClassCategory[]> {
  const path = servicePath('midl', 'class/category');
  const response = await api.get<unknown>(path);
  const rows = extractClassCategories(response.data);

  const out: ClassCategory[] = [];
  for (const row of rows) {
    const id = Number(row.id ?? row.categoryId);
    if (!Number.isFinite(id) || id === -1) continue;
    const name = (row.name ?? row.categoryName ?? '').trim();
    out.push({ id, name });
  }

  if (import.meta.env.DEV) {
    console.info(
      `[boardsApi] GET ${path} → ${out.length} categor${out.length === 1 ? 'y' : 'ies'}`,
      out.length === 0 ? { rawResponse: response.data } : undefined,
    );
  }

  return out;
}

/**
 * Shape of the category row echoed back by `POST /midl/api/v3/class/category`.
 * The backend's field naming varies a little between envelopes, so we map
 * `id`/`categoryId` and `name`/`categoryName` defensively.
 */
export interface CreatedClassCategory {
  id: number;
  name: string;
}

interface CreateClassCategoryEnvelope {
  statusCode?: string;
  data?: {
    id?: number | string;
    categoryId?: number | string;
    name?: string;
    categoryName?: string;
  };
}

/**
 * POST /midl/api/v3/class/category
 *
 * Creates a new profile class category. Contract (Swagger):
 *   POST …/class/category
 *   content-type: application/json
 *   body: { "name": "<string>" }
 *
 * Wired from the "Add category" action inside the
 * Manage categories popup. Returns the created category with its real
 * backend id so the UI can treat it as persisted.
 */
export async function createClassCategory(
  request: CreateClassCategoryRequest,
): Promise<CreatedClassCategory | null> {
  const name = request.name.trim();
  const path = servicePath('midl', 'class/category');
  const body = { name };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<
    CreateClassCategoryEnvelope | CreateClassCategoryEnvelope['data']
  >(path, body);
  const payload = response.data as CreateClassCategoryEnvelope | undefined;
  if (!payload) return null;

  const data =
    'data' in payload && payload.data
      ? payload.data
      : (payload as CreateClassCategoryEnvelope['data']);
  if (!data) return null;

  const rawId = data.id ?? data.categoryId;
  const id = Number(rawId);
  if (!Number.isFinite(id)) {
    return null;
  }
  return {
    id,
    name: data.name ?? data.categoryName ?? name,
  };
}

// ---------------------------------------------------------------------------
// Class category → ASSIGN a class (board) to a category / RENAME
// ---------------------------------------------------------------------------

export interface AssignClassToCategoryRequest {
  /** Numeric board (class) id to (re)assign. */
  readonly boardId: number | string;
  /**
   * Target category id. `-1` (the login `boardWithCategory` "uncategorised"
   * marker) moves the class back out of every named category.
   */
  readonly categoryId: number | string;
  /**
   * 1-based slot within the category. The backend requires a positive integer
   * and rejects `0` (`"Position is postive integer value"`).
   */
  readonly position: number;
}

/**
 * POST /midl/api/v3/class/category/addclass
 *
 * Assigns ONE board to a category at a position. Verified contract (Swagger):
 *   POST …/class/category/addclass
 *   body: AddClassInCategoryReqDto
 *     { "boardId": <int>, "categoryId": <int>, "position": <int> }
 *
 * `categoryId === -1` clears the assignment (back to uncategorised), mirroring
 * the login payload's uncategorised marker. `position` must be >= 1 — the
 * server rejects `0` — so we clamp defensively.
 */
export interface ClassCategoryMapping {
  readonly boardId: number;
  readonly categoryId: number;
  readonly boardCategoryId: number;
  readonly position: number;
}

function extractClassCategoryMapping(payload: unknown): ClassCategoryMapping | null {
  if (!payload || typeof payload !== 'object') return null;
  const root = payload as Record<string, unknown>;
  const row =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const boardId = Number(row.boardId);
  const categoryId = Number(row.categoryId);
  const boardCategoryId = Number(row.boardCategoryId);
  const position = Number(row.position);
  return [boardId, categoryId, boardCategoryId, position].every(Number.isFinite)
    ? { boardId, categoryId, boardCategoryId, position }
    : null;
}

export async function assignClassToCategory(
  request: AssignClassToCategoryRequest,
): Promise<ClassCategoryMapping | null> {
  const { boardId, categoryId, position } = request;
  const path = servicePath('midl', 'class/category/addclass');
  const numericPosition = Number(position);
  const body = {
    boardId: Number(boardId),
    categoryId: Number(categoryId),
    // Server contract: positive integer only (rejects 0).
    position: Number.isFinite(numericPosition) && numericPosition >= 1 ? numericPosition : 1,
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<unknown>(path, body);
  return extractClassCategoryMapping(response.data);
}

export interface MoveClassToCategoryRequest extends AssignClassToCategoryRequest {
  /** Existing board-category mapping id (not the board or category id). */
  readonly boardCategoryId: number | string;
}

/** PUT /midl/api/v3/class/category/addclass/{boardCategoryId}. */
export async function moveClassToCategory(
  request: MoveClassToCategoryRequest,
): Promise<ClassCategoryMapping | null> {
  const { boardCategoryId, boardId, categoryId, position } = request;
  const path = servicePath('midl', `class/category/addclass/${boardCategoryId}`);
  const response = await api.put<unknown>(path, {
    boardId: Number(boardId),
    categoryId: Number(categoryId),
    position: Math.max(1, Math.trunc(Number(position) || 1)),
  });
  return extractClassCategoryMapping(response.data);
}

export interface SortClassInCategoryRequest {
  readonly boardCategoryId: number | string;
  readonly newPosition: number;
}

/** PUT /midl/api/v3/class/category/class/sort/{boardCategoryId}. */
export async function sortClassInCategory(request: SortClassInCategoryRequest): Promise<void> {
  const path = servicePath('midl', `class/category/class/sort/${request.boardCategoryId}`);
  await api.put(path, { newPosition: Math.max(1, Math.trunc(Number(request.newPosition) || 1)) });
}

export interface SortClassCategoryRequest {
  readonly categoryId: number | string;
  readonly newPosition: number;
}

/** PUT /midl/api/v3/class/category/sort/{categoryId}. */
export async function sortClassCategory(request: SortClassCategoryRequest): Promise<void> {
  const path = servicePath('midl', `class/category/sort/${request.categoryId}`);
  await api.put(path, {
    newPosition: Math.max(1, Math.trunc(Number(request.newPosition) || 1)),
  });
}

export interface RenameClassCategoryRequest {
  /** Numeric category id to rename. */
  readonly categoryId: number | string;
  /** New display name. */
  readonly name: string;
}

/**
 * PUT /midl/api/v3/class/category/{categoryId}
 *
 * Renames a category. Verified contract (Swagger):
 *   PUT …/class/category/{id}
 *   body: CategoryRequestDto { "name": "<string>" }
 */
export async function renameClassCategory(request: RenameClassCategoryRequest): Promise<void> {
  const { categoryId, name } = request;
  const path = servicePath('midl', `class/category/${categoryId}`);
  const body = { name: name.trim() };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] PUT ${path}`, body);
  }

  await api.put(path, body);
}

export interface CategoryBulkActionRequest {
  readonly categoryId: number | string;
  readonly action: 'archive' | 'delete';
}

/** Archive a category and its classes, or permanently delete the active category/classes. */
export async function mutateClassCategory(request: CategoryBulkActionRequest): Promise<void> {
  const id = encodeURIComponent(String(request.categoryId));
  if (request.action === 'archive') {
    await api.put(servicePath('midl', `class/category/isArchive/${id}`), undefined, {
      params: { isArchive: true },
    });
    return;
  }
  await api.delete(servicePath('midl', `class/category/${id}`), {
    params: { isArchive: false },
  });
}

// ---------------------------------------------------------------------------
// Roster → CSV bulk add
// ---------------------------------------------------------------------------

export interface CsvNamelistRow {
  readonly fullname: string;
  readonly studentId?: string;
}

export interface AddCsvNamelistEntriesRequest {
  /** Numeric class/board id. */
  readonly boardId: number | string;
  /** Parsed CSV rows (fullname + optional studentId). */
  readonly rows: readonly CsvNamelistRow[];
}

/**
 * POST /midl/api/v3/class/namelist/csv/<boardId>
 *
 * Bulk-adds the rows parsed from a CSV upload into the class roster.
 * Verified contract (from the live web curl):
 *   POST …/class/namelist/csv/{boardId}
 *   body: { "data": [ { "fullname": "<string>", "studentId": "<string>" }, … ] }
 *
 * Note: the backend expects `studentId` to always be present in each row
 * (empty string when unknown) — mirror that exactly to stay compatible
 * with the mobile/web clients.
 */
export async function addCsvNamelistEntries(request: AddCsvNamelistEntriesRequest): Promise<void> {
  const { boardId, rows } = request;
  const path = servicePath('midl', `class/namelist/csv/${boardId}`);
  const body = {
    data: rows.map((row) => ({
      fullname: row.fullname,
      studentId: row.studentId?.trim() ?? '',
    })),
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, { boardId, rowCount: rows.length, body });
  }

  try {
    const response = await api.post(path, body);
    if (import.meta.env.DEV) {
      console.info(`[boardsApi] POST ${path} SUCCESS`, {
        status: response.status,
        data: response.data,
      });
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`[boardsApi] POST ${path} FAILED`, {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        status: (error as { response?: { status?: number } })?.response?.status,
        responseData: (error as { response?: { data?: unknown } })?.response?.data,
      });
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Members → "Add member" picker (invite from network / relatives)
// ---------------------------------------------------------------------------

/**
 * Shape of a single user returned by
 * `GET /userservice/api/v3/user/myRelative?...&actionFor=inviteMember`.
 *
 * This is the candidate list shown in the "Add members" modal — the people
 * the current user can invite into the class (their relatives / network).
 */
export interface AddableBoardMemberRaw {
  id?: number;
  userId?: number;
  userTypeId?: number;
  name?: string;
  surName?: string;
  username?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
  relation?: string;
}

interface AddableBoardMembersEnvelope {
  statusCode?: string;
  data?: AddableBoardMemberRaw[];
}

/**
 * Build the candidate display name from `name` + `surName`.
 *
 * Mirrors `fetchMyRelativeContacts.buildDisplayName`: some MyU payloads stash
 * the numeric user id in `surName`, so ignore an all-digits surname.
 */
function buildAddableDisplayName(user: AddableBoardMemberRaw): string {
  const first = (user.name ?? '').trim();
  const rawLast = (user.surName ?? '').trim();
  const last = rawLast && !/^\d+$/.test(rawLast) ? rawLast : '';
  const combined = `${first} ${last}`.trim();
  return combined || user.username || 'Member';
}

function toAddableClassMember(user: AddableBoardMemberRaw): AddableClassMember | null {
  const id = user.id ?? user.userId;
  if (id == null) return null;

  const name = buildAddableDisplayName(user);
  const subtitle = user.username
    ? `@${user.username.replace(/^@/, '')}`
    : (user.relation ?? '').trim() || undefined;

  return {
    id,
    userTypeId: user.userTypeId,
    name,
    initials: getInitials(name) || '?',
    avatar: user.profileImageThumbnail || user.profileImage || undefined,
    subtitle,
  };
}

export interface FetchAddableBoardMembersOptions {
  /** Server-side search term (optional). */
  readonly query?: string;
  /** Abort signal so React Query can cancel stale searches. */
  readonly signal?: AbortSignal;
}

/**
 * GET /userservice/api/v3/user/myRelative
 *   ?boardId=<id>&query=<q>&actionFor=inviteMember&size=50&page=0&debug=true
 *
 * Returns the list of users the current member can invite into the class,
 * mapped into the UI's {@link AddableClassMember} shape. Malformed rows are
 * silently dropped.
 */
export async function fetchAddableBoardMembers(
  boardId: number | string,
  options: FetchAddableBoardMembersOptions = {},
): Promise<AddableClassMember[]> {
  const { query, signal } = options;
  const path = servicePath('user', 'user/myRelative');

  const response = await api.get<AddableBoardMembersEnvelope | AddableBoardMemberRaw[]>(path, {
    params: {
      boardId,
      actionFor: 'inviteMember',
      size: 50,
      page: 0,
      debug: true,
      ...(query && query.trim() ? { query: query.trim() } : {}),
    },
    signal,
  });

  const body = response.data;
  const rows: AddableBoardMemberRaw[] = Array.isArray(body) ? body : (body?.data ?? []);
  const members = rows
    .map(toAddableClassMember)
    .filter((member): member is AddableClassMember => member !== null);

  if (import.meta.env.DEV) {
    console.info(
      `[boardsApi] GET ${path}?boardId=${boardId}&actionFor=inviteMember → ${members.length} candidate(s)`,
      members.length === 0 ? { rawResponse: response.data } : undefined,
    );
  }

  return members;
}

export interface AddBoardMembersRequest {
  /** Numeric class/board id. */
  readonly boardId: number | string;
  /** Numeric id of the current authenticated user performing the add. */
  readonly addedBy: number | string;
  /** Numeric MyU user ids of the candidates being added. */
  readonly members: readonly (number | string)[];
}

/**
 * POST /midl/api/v3/board/add-members
 *
 * Adds the selected candidates to the class. Verified contract (from the
 * live web curl):
 *   POST …/board/add-members
 *   body: { "boardId": <number>, "addedBy": <number>, "members": [<number>, …] }
 *   → { "statusCode": "2XX" }
 */
export async function addBoardMembers(request: AddBoardMembersRequest): Promise<void> {
  const { boardId, addedBy, members } = request;
  const path = servicePath('midl', 'board/add-members');
  const body = {
    boardId: Number(boardId),
    addedBy: Number(addedBy),
    members: members.map((id) => Number(id)),
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  await api.post(path, body);
}

// ---------------------------------------------------------------------------
// Member role / removal actions ("Make admin" / "Remove from class")
// ---------------------------------------------------------------------------

/**
 * Board detail returned by the admin invitation / cancel / remove endpoints.
 * Only the bits we read (the resolved `admin[]` roster) are typed; the rest of
 * the (large) board payload is passed through untouched.
 */
export interface BoardAdminUser {
  id: number;
  name?: string;
  username?: string;
  profileImage?: string;
  profileImageThumbnail?: string;
}

export interface BoardAdminActionResult {
  id?: number;
  noOfAdmins?: number;
  admin?: BoardAdminUser[];
  [key: string]: unknown;
}

function unwrapBoardAdminResult(body: unknown): BoardAdminActionResult {
  if (body && typeof body === 'object') {
    const envelope = body as { data?: unknown };
    if (envelope.data && typeof envelope.data === 'object') {
      return envelope.data as BoardAdminActionResult;
    }
    return body as BoardAdminActionResult;
  }
  return {};
}

export interface InviteBoardAdminRequest {
  /** Numeric class/board id. */
  readonly boardId: number | string;
  /** Numeric MyU user id of the member being invited to admin. */
  readonly invitedTo: number | string;
  /** Numeric MyU user id of the current user sending the invite. */
  readonly invitedBy: number | string;
}

/**
 * POST /midl/api/v3/board/admin/invitation
 *
 * Sends an "become an admin" invitation to a class member. The member stays
 * *pending* until they accept. Verified contract:
 *   body: { "invitedTo": <number>, "boardId": <number>, "invitedBy": <number> }
 *   → { "statusCode": "2XX", "data": <board detail> }
 */
export async function inviteBoardAdmin(
  request: InviteBoardAdminRequest,
): Promise<BoardAdminActionResult> {
  const { boardId, invitedTo, invitedBy } = request;
  const path = servicePath('midl', 'board/admin/invitation');
  const body = {
    invitedTo: Number(invitedTo),
    boardId: Number(boardId),
    invitedBy: Number(invitedBy),
  };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<unknown>(path, body);
  const result = unwrapBoardAdminResult(response.data);

  if (import.meta.env.DEV) {
    // Surface the full response so we can see exactly where the invitation's
    // requestId lives (the top-level `id` is the board id, not the invite id).
    console.info('[boardsApi] admin invitation response', response.data);
  }

  return result;
}

export interface CancelBoardAdminInvitationRequest {
  /** Numeric id of the pending admin-invitation request to cancel. */
  readonly requestId: number | string;
  /** Numeric MyU user id of the current user cancelling the invite. */
  readonly cancelledBy: number | string;
}

/**
 * POST /midl/api/v3/board/admin/cancel-invitation
 *
 * Cancels a still-pending admin invitation. Verified contract:
 *   body: { "requestId": <number>, "cancelledBy": <number> }
 *   → { "statusCode": "2XX", "data": <board detail> }
 */
export async function cancelBoardAdminInvitation(
  request: CancelBoardAdminInvitationRequest,
): Promise<BoardAdminActionResult> {
  const { requestId, cancelledBy } = request;
  const path = servicePath('midl', 'board/admin/cancel-invitation');
  const body = { requestId: Number(requestId), cancelledBy: Number(cancelledBy) };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<unknown>(path, body);
  return unwrapBoardAdminResult(response.data);
}

export interface RemoveBoardAdminRequest {
  /** Numeric admin-record id (the board-join record id) for the user being demoted. */
  readonly id: number | string;
  /** Numeric MyU user id of the current user performing the removal. */
  readonly removedBy: number | string;
}

/**
 * POST /midl/api/v3/board/admin/remove
 *
 * Demotes an accepted admin back to a regular member. Verified contract:
 *   body: { "id": <admin-record-id>, "removedBy": <number> }
 *   → { "statusCode": "2XX", "data": <board detail> }
 */
export async function removeBoardAdmin(
  request: RemoveBoardAdminRequest,
): Promise<BoardAdminActionResult> {
  const { id, removedBy } = request;
  const path = servicePath('midl', 'board/admin/remove');
  const body = { id: Number(id), removedBy: Number(removedBy) };

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] POST ${path}`, body);
  }

  const response = await api.post<unknown>(path, body);
  return unwrapBoardAdminResult(response.data);
}

export interface RemoveBoardMemberRequest {
  /** Numeric class/board id the member belongs to. */
  readonly boardId: number | string;
  /** Numeric MyU user id of the member being removed. */
  readonly userId: number | string;
}

/**
 * DELETE /midl/api/v3/board/{boardId}/join/{userId}?debug=true
 *
 * Removes a member from the class (revokes their join). Verified contract:
 *   DELETE …/board/<boardId>/join/<userId>?debug=true  (no request body)
 *   → { "statusCode": "2XX", "data": <join detail> }
 */
export async function removeBoardMember(request: RemoveBoardMemberRequest): Promise<void> {
  const { boardId, userId } = request;
  const path = servicePath('midl', `board/${boardId}/join/${userId}`);

  if (import.meta.env.DEV) {
    console.info(`[boardsApi] DELETE ${path}?debug=true`);
  }

  await api.delete(path, { params: { debug: true } });
}

// ---------------------------------------------------------------------------
// Pending admin-invitation lookup
// ---------------------------------------------------------------------------

/** Array field names a board payload might use to expose *sent* admin invites. */
const PENDING_ADMIN_INVITE_FIELDS = [
  'sentAdminInvitation',
  'sentAdminInvitations',
  'adminInvitation',
  'adminInvitations',
  'adminInvites',
  'pendingAdmin',
  'pendingAdmins',
  'pendingAdminInvitation',
  'pendingAdminInvitations',
  'adminRequest',
  'adminRequests',
] as const;

/** Keys that hold the invitation *record* id inside a pending-invite entry. */
const REQUEST_ID_FIELDS = ['requestId', 'id', 'invitationId', 'adminRequestId'] as const;
/** Keys that hold the *invited user's* id inside a pending-invite entry. */
const INVITED_USER_FIELDS = ['invitedTo', 'userId', 'memberId', 'id'] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function numberFrom(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const k of keys) {
    const v = record[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v);
  }
  return undefined;
}

/**
 * Scan a board detail payload for the pending admin-invitation `requestId`
 * addressed to `userId`. The invitation POST only echoes the board (whose top
 * level `id` is the *board* id, not an invite id), so cancelling needs us to
 * find the real per-invite record id from whichever array the board exposes.
 *
 * Returns `undefined` when no matching pending invite is found.
 */
export function resolvePendingAdminRequestId(
  board: unknown,
  userId: number | string,
): number | undefined {
  const root = asRecord(board);
  if (!root) return undefined;
  const target = Number(userId);

  for (const field of PENDING_ADMIN_INVITE_FIELDS) {
    const list = root[field];
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      const entry = asRecord(raw);
      if (!entry) continue;
      // Match on the invited user id; some payloads nest the user object.
      const invitedUser = asRecord(entry.invitedTo) ?? asRecord(entry.user) ?? entry;
      const invitedId = numberFrom(invitedUser, INVITED_USER_FIELDS);
      if (invitedId !== target) continue;
      const requestId = numberFrom(entry, REQUEST_ID_FIELDS);
      if (requestId != null) return requestId;
    }
  }
  return undefined;
}

/**
 * GET /midl/api/v3/board/{boardId} → resolve the pending admin-invitation
 * `requestId` for `userId`, if the board detail exposes it. Used as a fallback
 * when the invitation response didn't surface the id directly.
 */
export async function fetchPendingAdminRequestId(
  boardId: number | string,
  userId: number | string,
): Promise<number | undefined> {
  const detail = await fetchBoardDetail(boardId);
  const requestId = resolvePendingAdminRequestId(detail, userId);

  if (import.meta.env.DEV) {
    console.info(
      `[boardsApi] resolve pending admin requestId board=${boardId} user=${userId} → ${requestId ?? 'not found'}`,
    );
    if (requestId == null) {
      // Dump the board detail so we can locate where the pending invite id
      // actually lives if the heuristic field scan missed it.
      console.info('[boardsApi] board detail (admin requestId lookup)', detail);
    }
  }

  return requestId;
}

export type BoardJoinRequestStatus = typeof BOARD_REQUEST_PENDING | typeof BOARD_REQUEST_JOINED;

export interface RequestBoardJoinParams {
  boardId: number | string;
  joinedBy: number | string;
  /** 1 = requested, 2 = joined. */
  isJoined: BoardJoinRequestStatus;
}

/**
 * POST /midl/api/v3/board/join
 *
 * Creates a join request or immediately joins the board, depending on the
 * backend's privacy/admin rules. The response `data.isJoined` is authoritative.
 */
export async function joinBoard(
  params: Omit<RequestBoardJoinParams, 'isJoined'>,
): Promise<BoardMembershipResponseData> {
  const { data } = await api.post<BoardMembershipEnvelope | BoardMembershipResponseData>(
    servicePath('midl', 'board/join', { version: 'v3' }),
    {
      boardId: Number(params.boardId),
      joinedBy: Number(params.joinedBy),
    },
  );

  return normalizeBoardMembershipResponse(data, BOARD_REQUEST_PENDING);
}

/**
 * DELETE /midl/api/v3/board/join
 *
 * Leaves a joined board or cancels a pending join request. Axios sends the
 * Android-equivalent body through the `data` request config key.
 */
export async function unjoinBoard(
  params: Omit<RequestBoardJoinParams, 'isJoined'>,
): Promise<BoardMembershipResponseData> {
  const { data } = await api.delete<BoardMembershipEnvelope | BoardMembershipResponseData>(
    servicePath('midl', 'board/join', { version: 'v3' }),
    {
      data: {
        boardId: Number(params.boardId),
        joinedBy: Number(params.joinedBy),
      },
    },
  );

  return normalizeBoardMembershipResponse(data, BOARD_REQUEST_NOT_JOINED);
}

export interface ToggleBoardMembershipParams {
  boardId: number | string;
  joinedBy: number | string;
  currentStatus: BoardMembershipStatus;
}

/**
 * Android-parity membership toggle:
 *   • 0 → POST /board/join
 *   • 1/2 → DELETE /board/join
 */
export async function toggleBoardMembership({
  boardId,
  joinedBy,
  currentStatus,
}: ToggleBoardMembershipParams): Promise<BoardMembershipResponseData> {
  if (currentStatus === BOARD_REQUEST_NOT_JOINED) {
    return joinBoard({ boardId, joinedBy });
  }
  return unjoinBoard({ boardId, joinedBy });
}

/**
 * @deprecated Prefer {@link toggleBoardMembership}. Kept for older callers;
 * MyU now expects POST for current-user join/request creation.
 */
export async function requestBoardJoin(
  params: RequestBoardJoinParams,
): Promise<BoardMembershipResponseData> {
  const response = await joinBoard({
    boardId: Number(params.boardId),
    joinedBy: Number(params.joinedBy),
  });
  return {
    ...response,
    isJoined: toBoardMembershipStatus(response.isJoined) ?? params.isJoined,
  };
}
